import { z } from 'zod';

/** Fleet reference data: the vocabulary a deployment's trucks, trailers and documents are described with (gitaumoses4/chukua#10). */

/**
 * What a truck of a class looks like behind the cab (the tyre layout's 3D
 * model draws it). A new style is a migration (CHECK on truck_classes) and a
 * builder in the web model.
 */
export const TRUCK_BODY_STYLES = ['tractor', 'tipper', 'box', 'tanker', 'dropside'] as const;
export type TruckBodyStyle = (typeof TRUCK_BODY_STYLES)[number];

/** What a trailer type is built as. The skeletal's deck length is part of the style because nothing else on the type says it. */
export const TRAILER_BODY_STYLES = ['skeletal_40', 'skeletal_20', 'flatbed', 'sidewall', 'tanker', 'lowbed', 'reefer', 'tipper', 'drawbar'] as const;
export type TrailerBodyStyle = (typeof TRAILER_BODY_STYLES)[number];

/**
 * What a truck of a class can pull (gitaumoses4/chukua#390): a prime mover's fifth wheel takes a semi-trailer's kingpin, and a
 * rigid truck's drawbar hitch takes a drawbar trailer. `none` tows nothing (a box body). Configuration, not a code branch: an
 * admin sets it on the class. A new value is a migration (CHECK on truck_classes).
 */
export const TRUCK_COUPLINGS = ['fifth_wheel', 'drawbar_hitch', 'none'] as const;
export type TruckCoupling = (typeof TRUCK_COUPLINGS)[number];

/** How a trailer of a type hitches to the truck pulling it (CHECK on trailer_types). */
export const TRAILER_HITCHES = ['kingpin', 'drawbar'] as const;
export type TrailerHitch = (typeof TRAILER_HITCHES)[number];

/**
 * The most a truck, trailer or truck class may be rated to carry. Above any vehicle on the road and inside the `numeric(6,2)`
 * columns, so a weight typed in kilograms (50,000 for 50 t) is refused with a reason instead of overflowing the database.
 */
export const MAX_CAPACITY_TONNES = 1000;

export const capacityTonnesSchema = z
  .number()
  .positive()
  .max(MAX_CAPACITY_TONNES, `Capacity is in tonnes, up to ${MAX_CAPACITY_TONNES.toLocaleString('en')} t`);

/**
 * A starting fuel consumption in km per litre (gitaumoses4/chukua#110), for a truck or a class until the truck has trips to
 * average. Above any truck on the road and inside the `numeric(5,2)` columns, so litres per 100 km typed by mistake (a truck's 35) is refused.
 */
export const MAX_KM_PER_LITRE = 30;

export const kmPerLitreSchema = z.number().positive().max(MAX_KM_PER_LITRE, `Consumption is in km per litre, up to ${MAX_KM_PER_LITRE}`);

/** The colour a truck or trailer is painted, drawn on its 3D model everywhere it appears. White by default, as most fleets' are. */
export const DEFAULT_VEHICLE_COLOR = '#F4F4F1';

/** The colours the truck and trailer forms offer first; any other is a custom pick. */
export const VEHICLE_COLOR_PRESETS = [
  { name: 'White', value: DEFAULT_VEHICLE_COLOR },
  { name: 'Silver', value: '#B9BCBE' },
  { name: 'Black', value: '#26282C' },
  { name: 'Red', value: '#B3362C' },
  { name: 'Blue', value: '#2E64EB' },
  { name: 'Navy', value: '#22324F' },
  { name: 'Green', value: '#3F7A4A' },
  { name: 'Yellow', value: '#E2B13C' },
  { name: 'Orange', value: '#D9772E' },
] as const;

/** `#rrggbb`, stored upper case so the same colour is always written the same way. */
export const vehicleColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'A colour as #rrggbb')
  .transform((value) => value.toUpperCase());

/** A vehicle as the login page's rigs show it: the plate it carries and the colour it is painted. */
export interface PlatedVehicle {
  registration: string;
  color: string;
}

/**
 * A few of the company's container rigs (`GET /fleet/plates`, public): the plates and colours the
 * login page's rigs carry. A random sample each call, tractors and container trailers apart; plate
 * and paint only, never what a vehicle is, where it is or anything else about it.
 */
export interface FleetPlates {
  trucks: PlatedVehicle[];
  trailers: PlatedVehicle[];
}

export const truckClassInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(120),
  typicalCapacityTonnes: capacityTonnesSchema,
  /** The licence class a driver needs for this class of truck (dispatch compliance check). Omit to keep it (null on a new class); null clears it. */
  requiredLicenceClass: z.string().trim().min(1).max(16).nullable().optional(),
  /** Omit to keep it (`tractor` on a new class). */
  bodyStyle: z.enum(TRUCK_BODY_STYLES).optional(),
  /** What the class can pull. Omit to keep it (on a new class, what its body style usually has: `defaultTruckCoupling`). */
  coupling: z.enum(TRUCK_COUPLINGS).optional(),
  /** What a truck of this class burns until it has trips of its own to average (gitaumoses4/chukua#110). Omit to keep it (null on a new class); null clears it. */
  defaultKmPerLitre: kmPerLitreSchema.nullable().optional(),
});
export type TruckClassInput = z.infer<typeof truckClassInputSchema>;

export const trailerTypeInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(120),
  /** Container size codes this type carries (gitaumoses4/chukua#48). Empty means undeclared: the job-fit check does not judge it. */
  containerSizeCodes: z.array(z.string().trim().min(1).max(32)).default([]),
  /** Omit to keep it (`skeletal_40` on a new type). */
  bodyStyle: z.enum(TRAILER_BODY_STYLES).optional(),
  /** How the type hitches. Omit to keep it (on a new type, what its body style usually has: `defaultTrailerHitch`). */
  hitch: z.enum(TRAILER_HITCHES).optional(),
});
export type TrailerTypeInput = z.input<typeof trailerTypeInputSchema>;

export const DOCUMENT_TYPE_APPLIES_TO = ['truck', 'trailer', 'driver'] as const;
export type DocumentTypeAppliesTo = (typeof DOCUMENT_TYPE_APPLIES_TO)[number];

export const documentTypeInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(120),
  appliesTo: z.enum(DOCUMENT_TYPE_APPLIES_TO),
  legalHardBlock: z.boolean(),
  crossBorderOnly: z.boolean(),
  typicalValidityDays: z.number().int().positive(),
  reminderLeadDays: z.number().int().positive(),
});
export type DocumentTypeInput = z.infer<typeof documentTypeInputSchema>;
